import { CareEnvironment, CarePhase, FundingPolicyType } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  growthFactor,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import {
  applyIndemnitySurplus,
  applyOpeningSurplusToCost,
} from '../sharedSurplus';
import {
  Claimant,
  JointWaitingPeriod,
  partnerWaitingPeriodSeed,
  seedJointWaitingPeriod,
} from '../jointClaimant';

// ---------------------------------------------------------------------------
// Traditional / standalone LTC insurance — generic reimbursement product.
// Also models CareScout Care Assurance, which runs the identical generic
// standalone-LTC calc path (one strategy serves both).
//
// SINGLE POOL, NO DEATH BENEFIT:
// - `policyMaximumBenefitAmount` IS the lifetime LTC dollar pool (the
//   "Coverage Maximum" on CareScout, p.2). Unlike the asset-based hybrids,
//   this is NOT a death benefit — traditional LTC has no death benefit at
//   all. We seed `baseBenefitPool` from it (less utilized-to-date) and keep
//   `extensionOfBenefitsPool = 0` (no second pool / continuation rider) and
//   `deathBenefit = 0` (no DB pool). `finalizeDeathBenefit` returns 0 and
//   every month emits `deltas.deathBenefit = { reduction: 0, flooredAt-
//   Minimum: false }`.
//
// PAYOUT MODEL — reimbursement (default) or cash indemnity:
// - Reimbursement: pay `min(eligibleCost, proRatedDailyCap)` from the pool,
//   where `proRatedDailyCap = monthlyCap × (eligibleDays/30)` and
//   `monthlyCap = monthlyBenefitForEnvironment × compoundFactor(rate, ...)`.
//   Excess care cost above the cap is out-of-pocket.
// - Indemnity (gated on `isIndemnityPolicyPayment === true`): pay
//   `min(proRatedCap, poolRemaining)` as cash regardless of actual cost,
//   drawn from the single pool. Matches the legacy indemnity tail; default
//   is reimbursement.
//
// DAILY-CAP INFLATION (at-issue-anchored, compound):
// - `dailyBenefit × compoundFactor(rate, capYears, policyYear)`. The per-
//   month dollar cap steps up on each policy anniversary, anchored to the
//   at-issue daily benefit — same mechanic the hybrids use for their cap.
//
// POOL INFLATION (REMAINING-BALANCE, the carrier-accurate fix):
// - CareScout p.2 states inflation "automatically increases your Daily
//   Benefit Amount and the Coverage Maximum *remaining* after any claim
//   payments." So the pool compounds on the REMAINING balance, not the at-
//   issue amount: at each policy anniversary (gated on `pool > 0` and within
//   `capYears`) we accrue `inflationAccrual = openPool × rate`. This differs
//   from the hybrids' at-issue-anchored accrual on purpose — it is carrier-
//   accurate for traditional LTC. The universal identity
//   `close = open + inflationAccrual − depletion` still holds.
// - For SIMPLE inflation (`isSimpleInflationProtection`), we accrue a
//   constant increment `atIssuePool × rate` each anniversary instead of
//   compounding the remaining balance — simple inflation grows the benefit
//   by a fixed dollar step per year, not as a percentage of the current
//   (drawn-down) balance.
//
// PRE-CLAIM POOL INFLATION:
// - Anniversaries between policy issue and claim start are not iterated (the
//   projection only spans claim months), so the undrawn pool has already
//   compounded N−1 anniversaries when a claim starts in policy year N. We
//   pre-inflate the opening pool in `initClaimState` via
//   `compoundFactor(rate, capYears, claimStartPolicyYear)` (compound) so the
//   opening balance matches the carrier's current pool. (Simple inflation's
//   pre-claim growth is also folded in via a linear factor — see below.)
//
// 2-ADL GATE + WAITING PERIOD (elimination / deductible period):
// - Standard `computeNon2AdlDaysThisRow` → `consumeWaitingPeriodDaysFor-
//   Environment`. EP days are strictly out-of-pocket; there is NO
//   retroactive lump (CareScout / generic trad-LTC default has none), so we
//   pass `retroactiveLumpPaid: 0` to the milestone deriver.
//
// `policyBenefitPeriodMonths` is NOT a cap here — the dollar pool is the
// only cap. Like Asset Care, neither a per-claim nor a per-month carry-
// forward is needed beyond the universal slots, so both state and deltas
// shapes are empty.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// JOINT ShareCare 1/3 shared-pool model (NY Life SecureCare joint, NGL
// EssentialLTC joint):
//
// DATA CONVENTION (P2-22): `policyMaximumBenefitAmount` stores the COMBINED
// household total for both spouses — both individual pools plus the shared
// pool (typically one spouse's lifetime maximum × 3). A per-insured figure
// stored here would understate the model 3×. The manual insurance form and
// the upload field map both state this convention; the ÷3 split below
// requires it (same as legacy calcFundingSourceDerivatives.ts:1500-1501).
//
// The total dollar pool splits into THREE equal 1/3 parts — the client's
// individual pool, the partner's individual pool, and a shared pool. Each
// spouse draws from THEIR OWN individual pool first; once that pool is
// exhausted, further draws come from the SHARED pool. The shared pool is
// common to both spouses (legacy `sharedPoolUtilized` is a single counter that
// both spouses spill into). Replicates the legacy ShareCare math in
// calcFundingSourceDerivatives.ts:
//   - individualPoolAmount = (policyMaximumBenefitAmount −
//     policyBenefitUtilizedToDate) / 3  (line ~1445-1446); the shared pool is
//     the same 1/3 amount.
//   - each pool inflates via `policyInflationProtection` (line ~1450-1456,
//     `calcPolicyInflatedMaxBenefitToEndOfPhase`), reusing the same shared
//     `growthFactor` + remaining-balance anniversary accrual the single-life
//     path uses.
//   - a spouse spills into the shared pool once their individual pool is
//     exhausted (line ~1977-1981 `projectedCoverage − maxBenefit`); the shared
//     pool is exhausted when cumulative shared draw ≥ inflatedIndividualPool
//     (line ~2013-2021 `updateSharedPoolStatus`).
//
// The per-claimant individual pools + the shared pool live in `strategyState`.
// The universal MonthlyState.pools.baseBenefitPool surfaces the ACTIVE
// claimant's drawable capacity (their individual remaining + the shared
// remaining) so the universal per-pool identity (close = open + accrual −
// depletion) holds row-by-row and exhaustion/UI reads stay meaningful. The
// other spouse's individual pool is invisible to that identity (it lives only
// in strategyState). Single-life keeps `strategyState: {}` and the single
// `baseBenefitPool` exactly as before.
// ---------------------------------------------------------------------------

export interface TraditionalLtcJointPools {
  clientIndividual: number;
  partnerIndividual: number;
  shared: number;
}

export interface TraditionalLtcState {
  // Indemnity-surplus reserve. Indemnity policies pay a fixed monthly benefit
  // regardless of cost; the excess above eligible cost is banked here and
  // drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there. On
  // joint ShareCare the reserve is shared across the policy.
  indemnitySurplus: number;
  // Joint ShareCare only: the three 1/3 sub-pools (client individual, partner
  // individual, shared). Undefined on single-life.
  jointPools?: TraditionalLtcJointPools;
  // Joint only: per-claimant elimination-period balances. Single-life uses the
  // universal MonthlyState.waitingPeriod slot.
  jointWaitingPeriod?: JointWaitingPeriod;
  // Joint ShareCare only: cumulative dollars drawn from the SHARED 1/3 pool
  // across BOTH spouses, to date. An explicit running total (each row adds that
  // row's shared spill) rather than an inference from balances — the shared
  // sub-pool inflates its remaining balance each anniversary, so
  // `initial − remaining` would count inflation growth as usage. Lets the UI
  // show how much of the shared pool has been consumed. Undefined off ShareCare.
  sharedPoolDrawnToDate?: number;
}
export interface TraditionalLtcDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

// The joint standalone-LTC products that use the legacy ShareCare 1/3
// shared-pool model (NY Life SecureCare, NGL EssentialLTC, Thrivent LTC, Mutual
// of Omaha MutualCare — all joint). Single-life variants do NOT split. Must stay
// in sync with the joint ShareCare routing branch in `pickStrategy`
// (calcFundingSourceDerivatives.ts) — a type routed there but missing here would
// wrongly fall into the single-life branch and seed the full combined pool as one
// spouse's, overstating coverage ~3×.
function policyTypeHasSharedPool(policyType: FundingPolicyType | null | undefined): boolean {
  return (
    policyType === FundingPolicyType.nylJointSecureCare ||
    policyType === FundingPolicyType.jointNglEssentialLtc ||
    policyType === FundingPolicyType.jointThriventLongTermCareInsurance ||
    policyType === FundingPolicyType.jointMutualOfOmahaMutualcare
  );
}

interface InflationConfig {
  rate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    // `growthFactor` treats capYears <= 0 as uncapped; null/0 here means
    // lifetime/uncapped inflation, which maps cleanly onto that contract.
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// A standalone LTC policy with a null/undefined `policyMaximumBenefitAmount`
// is UNLIMITED, not $0 — matching legacy `isUnlimitedBenefit` (a missing max
// on a non-hybrid policy returns `unlimitedMaxBenefitMagicNumber` and flags
// `policyBenefitIsUnlimited`). A populated max (including 0) is a real cap.
function isUnlimitedPool(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyMaximumBenefitAmount == null;
}

// At-issue lifetime dollar pool. Unlimited policies seed the sentinel so
// payouts are never pool-limited; the client renders any pool >= the sentinel
// as "Unlimited". A populated max (including 0) is used verbatim.
function getAtIssuePool(ltcPolicy: PolicyFundingSource): number {
  if (isUnlimitedPool(ltcPolicy)) return unlimitedMaxBenefitMagicNumber;
  return ltcPolicy.policyMaximumBenefitAmount ?? 0;
}

export const traditionalLtcStrategy: PolicyProjectionStrategy<
  TraditionalLtcState,
  TraditionalLtcDeltas
> = {
  initClaimState(client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<TraditionalLtcState> {
    const atIssuePool = getAtIssuePool(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const inflation = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated
    // (the projection only spans claim months), so pre-inflate the pool to
    // its value at claim start. Claims starting in policy year 1 keep the
    // factor at 1.0. Utilized-to-date is subtracted after inflating, so it
    // reduces the already-grown pool.
    // Unlimited pools stay flat at the sentinel — no pre-claim inflation, no
    // utilized-to-date reduction (legacy's unlimited path returns the magic
    // number verbatim, before any of that logic runs).
    // Joint pre-inflates to the shared anchor (earliest claim start across both
    // spouses); single-life uses the session client's own claim-start year.
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(client as Client, 0);
    const poolAtClaimStart = isUnlimitedPool(ltcPolicy)
      ? atIssuePool
      : atIssuePool *
        growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, claimStartPolicyYear);

    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
        ltcPolicy.homeCareWaitingPeriodDays ??
        0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };

    // Joint ShareCare: split the (pre-inflated, less utilized) pool into three
    // equal 1/3 sub-pools — client individual, partner individual, shared.
    // Each spouse serves its own EP via the joint waiting-period state; any
    // `...DaysRemaining` partial credit belongs to the primary client's
    // in-flight claim only, the partner seeds from the full policy EP. Only
    // the two ShareCare product types split; generic joint
    // (jointLongTermCareInsurance) keeps the single combined pool below.
    const isJoint = jointAnchorPolicyYear != null;
    const isJointShareCare = isJoint && policyTypeHasSharedPool(ltcPolicy.policyType);
    if (isJointShareCare) {
      const oneThird = Math.max(poolAtClaimStart - utilizedToDate, 0) / 3;
      // The active claimant's drawable capacity (their individual + shared) is
      // surfaced on the universal slot so the per-pool identity holds. Joint
      // runs are session-client first, so seed with the client's view.
      return {
        pools: {
          baseBenefitPool: oneThird + oneThird,
          extensionOfBenefitsPool: 0,
          deathBenefit: 0,
        },
        waitingPeriod,
        strategyState: {
          indemnitySurplus: 0,
          jointPools: {
            clientIndividual: oneThird,
            partnerIndividual: oneThird,
            shared: oneThird,
          },
          sharedPoolDrawnToDate: 0,
          jointWaitingPeriod: seedJointWaitingPeriod(
            waitingPeriod,
            partnerWaitingPeriodSeed(ltcPolicy),
          ),
        },
      };
    }

    // Generic joint (jointLongTermCareInsurance) shares this single combined
    // pool but still needs per-spouse EP balances and the shared anchor clock
    // — seed jointWaitingPeriod so computeMonth runs its joint path.
    return {
      pools: {
        baseBenefitPool: isUnlimitedPool(ltcPolicy)
          ? atIssuePool
          : Math.max(poolAtClaimStart - utilizedToDate, 0),
        // Traditional LTC has no continuation-of-benefits pool and no death
        // benefit — both stay at 0 for the life of the projection.
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod,
      strategyState: isJoint
        ? {
            indemnitySurplus: 0,
            jointWaitingPeriod: seedJointWaitingPeriod(
              waitingPeriod,
              partnerWaitingPeriodSeed(ltcPolicy),
            ),
          }
        : { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy, jointAnchorPolicyYear): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const inflation = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization actually deducted from the LTC pool at claim start —
    // mirrors `initClaimState` (joint splits AFTER subtracting utilized, so the
    // policy-level deduction is the same `Math.min(utilized, poolAtClaimStart)`
    // for joint and single-life). Unlimited pools deduct nothing. The pool is
    // inflated to the SAME anchor `initClaimState` used (the shared joint
    // anchor when the partner claims first), so the reported cap matches the
    // amount actually deducted in the rare over-utilized edge case.
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(clientAsClient, 0);
    const poolAtClaimStart =
      getAtIssuePool(ltcPolicy) *
      growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, claimStartPolicyYear);
    const priorUtilization = isUnlimitedPool(ltcPolicy)
      ? 0
      : Math.min(utilizedToDate, poolAtClaimStart);
    return {
      strategyId: 'traditional-ltc',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Same sparse-field fallback the payout cap uses, so the labelled
      // per-environment benefits always match what the engine actually pays.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.home,
        ),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Single product-wide inflation rate drives both the daily-cap step and
      // the pool growth — populate the policy-wide and base-pool knobs from
      // it. There is no separate face/COB rate on traditional LTC, so leave
      // the EOB-pool knobs undefined (no second pool).
      inflationRate: inflation.rate,
      inflationCapYears: inflation.capYears,
      inflationIsSimple: inflation.isSimple,
      baseBenefitPoolInflationRate: inflation.rate,
      baseBenefitPoolInflationCapYears: inflation.capYears,
      baseBenefitPoolAtIssue: getAtIssuePool(ltcPolicy),
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // No death benefit on traditional LTC.
      minimumGuaranteedDeathBenefit: 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      isUnlimited: isUnlimitedPool(ltcPolicy),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const inflation = readInflation(ltcPolicy);
    const factor = growthFactor(
      inflation.rate,
      inflation.capYears,
      inflation.isSimple,
      policyYear,
    );

    const atIssuePool = getAtIssuePool(ltcPolicy);
    const inflatedMonthlyCap = baseMonthly * factor;
    // The snapshot's effective total LTC benefit reflects the inflated pool
    // ceiling assuming no draws this year (illustration "no claims paid"
    // framing). Actual remaining pool is carried on the month state. Unlimited
    // pools stay flat at the sentinel (the client renders >= sentinel as
    // "Unlimited"); only the daily/monthly cap inflates.
    const inflatedPool = isUnlimitedPool(ltcPolicy) ? atIssuePool : atIssuePool * factor;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      // No death benefit / continuation pool on traditional LTC.
      effectiveFaceAmount: 0,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: inflatedPool,
      monthlyBenefitCap: inflatedMonthlyCap,
      theoreticalLtcPoolCeiling: inflatedPool,
    };
  },

  computeMonth(
    input: MonthComputeInput<TraditionalLtcState, TraditionalLtcDeltas>,
  ): MonthlyEntry<TraditionalLtcState, TraditionalLtcDeltas> {
    const {
      client,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
    } = input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Joint-ness is read from the carried state (the per-claimant EP balances
    // are present), not the row tag, so a joint run always preserves both
    // spouses' state. ShareCare additionally carries the 1/3 sub-pools;
    // generic joint stays single-pool. Single-life keeps strategyState empty.
    const jointPoolsBefore = openState.strategyState.jointPools;
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const isJoint = jointWaitingPeriodBefore != null;
    const activeClaimant: Claimant = claimant ?? 'client';

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification. Joint rides the
    // shared anchor clock (both spouses on one monotonic policy-year clock).
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = isJoint
      ? resolveRowPolicyYear(clientAsClient, monthIndex, input.jointAnchorPolicyYear)
      : getCalendarPolicyYear(clientAsClient, monthIndex);

    // Each spouse serves its own EP: joint reads the active claimant's balance
    // from strategyState (the universal waitingPeriod slot is rewritten per
    // row and would otherwise carry the prior row's spouse).
    const activeWaitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;

    // The active claimant's drawable capacity on this row = their individual
    // pool + the shared pool. Surfaced on the universal baseBenefitPool slot so
    // the per-pool identity (close = open + accrual − depletion) holds.
    const activePoolBefore = jointPoolsBefore
      ? (activeClaimant === 'client'
          ? jointPoolsBefore.clientIndividual
          : jointPoolsBefore.partnerIndividual) + jointPoolsBefore.shared
      : openState.pools.baseBenefitPool;

    // The row's openState surfaces the active claimant's WP-before and drawable
    // pool so identity checks + UI read per-claimant. Single-life passes the
    // openState through unchanged.
    const rowOpenState: MonthlyState<TraditionalLtcState> = isJoint
      ? {
          ...openState,
          pools: { ...openState.pools, baseBenefitPool: activePoolBefore },
          waitingPeriod: activeWaitingPeriodBefore,
        }
      : openState;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate (see buildPrePolicyMonth). Single-life traditional LTC
    // floors its policy year at 1 via getCalendarPolicyYear, so this is
    // defensive there — but a joint partner row can start before the premium
    // year and must pay $0, full OOP, without touching the shared pool.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<TraditionalLtcState, TraditionalLtcDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    // Deductible / elimination period. The 90-day deductible applies
    // regardless of care setting (both home and facility WP buckets seeded to
    // the same value); the shared two-tier WP machinery handles per-setting
    // routing and the 1st-Day Home Care waiver (home bucket = 0) when elected.
    // Joint consumes the active claimant's per-spouse balance.
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      activeWaitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-month dollar cap (this year's inflation factor). `costMetrics` and
    //    `policyYear` were resolved up front for the pre-policy gate.
    const inflation = readInflation(ltcPolicy);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    // Daily cap inflates at-issue-anchored (compound for compound inflation;
    // linear step for simple), consistent with the pool's growth model.
    const monthlyCap =
      baseMonthly *
      growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, policyYear);

    // 3. Annual anniversary pool accrual on the REMAINING balance. Gated on
    //    "pool still > 0" so an exhausted pool stays exhausted. Compound
    //    inflation grows the remaining balance by `rate`; simple inflation adds
    //    a constant `atIssue × rate` step. Within capYears only.
    //
    //    Joint: each 1/3 sub-pool inflates independently on its OWN remaining
    //    balance (mirroring legacy `calcPolicyInflatedMaxBenefitToEndOfPhase`
    //    applied to the individual pool, with the shared pool tracked the same
    //    way). All three accrue once per shared policy-year boundary (gated on
    //    the global `priorMonth`). For simple inflation each sub-pool steps by
    //    its at-issue 1/3 × rate. The accrual surfaced into `base.inflation-
    //    Accrual` is only the active claimant's (their individual + shared) so
    //    the universal identity on `baseBenefitPool` balances; the other
    //    spouse's individual-pool accrual is internal to strategyState.
    // The merged joint stream contains only care rows, so when the spouses'
    // claims don't overlap the policy year can JUMP several anniversaries
    // between two consecutive rows (e.g. a year-2 client row straight to a
    // year-11 partner row). Accrue the FULL multi-year growth across that gap,
    // not a single year — otherwise a binding pool is under-inflated by every
    // skipped anniversary while the monthly cap (at-issue-anchored) already
    // reflects the full jump. Count the in-cap anniversaries crossed since the
    // prior row; for a contiguous single-life stream this is 1 at each
    // anniversary, 0 otherwise, and 0 once past the cap — reproducing the prior
    // single-year behavior exactly. Mirrors oaAssetCare's factor-difference
    // accrual, adapted to the remaining-balance model.
    const priorYear = priorMonth?.context.policyYear ?? policyYear;
    // A capYears-year rider applies capYears increases, the last one landing on
    // the anniversary entering policy year capYears + 1 — matching
    // `growthFactor`'s min(policyYear − 1, capYears) step convention, so the
    // in-claim pool agrees with `buildYearSnapshot` through the final cap year.
    const capEnd = inflation.capYears > 0 ? inflation.capYears + 1 : policyYear;
    const anniversariesCrossed =
      priorMonth != null ? Math.max(0, Math.min(policyYear, capEnd) - priorYear) : 0;
    const inflationActive =
      anniversariesCrossed > 0 && !isUnlimitedPool(ltcPolicy) && inflation.rate > 0;
    // Simple inflation grows each pool by a constant `atIssue × rate` step per
    // crossed anniversary — on the ORIGINAL at-issue amount in both claim
    // shapes (P2-21; carrier-typical simple-rider behavior). Prior utilization
    // reduces the seeded balances, never the step base: subtracting it here
    // (as the joint path once did) made the same policy accrue 3× differently
    // by claim shape. Single-life and generic joint: the full at-issue pool.
    // ShareCare: each 1/3 sub-pool steps by its at-issue 1/3 (matching the
    // single 1/3 individual-pool seed).
    const simpleStepBase = jointPoolsBefore
      ? getAtIssuePool(ltcPolicy) / 3
      : getAtIssuePool(ltcPolicy);
    const accrueSubPool = (balance: number): number => {
      if (!inflationActive || balance <= 0) return 0;
      // Compound: grow the remaining balance by `anniversariesCrossed` years of
      // compounding. Simple: add a flat at-issue step per crossed anniversary.
      return inflation.isSimple
        ? simpleStepBase * inflation.rate * anniversariesCrossed
        : balance * ((1 + inflation.rate) ** anniversariesCrossed - 1);
    };

    let clientIndividualAfterAccrual = 0;
    let partnerIndividualAfterAccrual = 0;
    let sharedAfterAccrual = 0;
    let inflationAccrual = 0;
    let poolAfterAccrual = 0;
    let activeIndividualAfterAccrual = 0;

    if (jointPoolsBefore) {
      const clientAccrual = accrueSubPool(jointPoolsBefore.clientIndividual);
      const partnerAccrual = accrueSubPool(jointPoolsBefore.partnerIndividual);
      const sharedAccrual = accrueSubPool(jointPoolsBefore.shared);
      clientIndividualAfterAccrual = jointPoolsBefore.clientIndividual + clientAccrual;
      partnerIndividualAfterAccrual = jointPoolsBefore.partnerIndividual + partnerAccrual;
      sharedAfterAccrual = jointPoolsBefore.shared + sharedAccrual;
      activeIndividualAfterAccrual =
        activeClaimant === 'client'
          ? clientIndividualAfterAccrual
          : partnerIndividualAfterAccrual;
      // The identity-relevant accrual is the active claimant's slice only
      // (their individual + shared), matching the surfaced baseBenefitPool.
      inflationAccrual =
        (activeClaimant === 'client' ? clientAccrual : partnerAccrual) + sharedAccrual;
      poolAfterAccrual = activeIndividualAfterAccrual + sharedAfterAccrual;
    } else {
      inflationAccrual = accrueSubPool(openState.pools.baseBenefitPool);
      // Unlimited pools never accrue (accrueSubPool returns 0 via isUnlimited).
      poolAfterAccrual = openState.pools.baseBenefitPool + inflationAccrual;
    }

    // 4. Payout: reimbursement (default) or cash indemnity.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromPool = 0;
    let indemnityPaid = 0;
    if (isIndemnity && poolAfterAccrual > 0 && eligibleDays > 0) {
      // Pay capped cash regardless of actual cost; cannot exceed the pool.
      const indemnityAmount = Math.min(proRatedCap, poolAfterAccrual);
      drawFromPool = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      // Reimbursement: pay min(eligibleCost, proRatedCap), drawn from pool.
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromPool = Math.min(wouldPay, poolAfterAccrual);
    }
    // Gross cash this row pays out. On indemnity this is the capped cash
    // regardless of cost; on reimbursement it is the reimbursed amount
    // (≤ eligible cost). The pool depletes by this gross cash; the surplus
    // model is a separate accounting reserve layered on top.
    const rowPayout = drawFromPool;

    // 4b. Indemnity-surplus accounting (per-row, unconditional). Cost-coverage
    //     is capped at eligible cost; cash above eligible cost banks as surplus
    //     and drains on later shortfall rows. On reimbursement-election
    //     policies the open surplus is always 0 and the reimbursed payout never
    //     exceeds eligible cost, so this is a numeric no-op (no accrual/drain).
    const policyExhausted = poolAfterAccrual <= 0;
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Exhausted = the (post-accrual) pool can't fund another dollar. A $0
      // payout alone is NOT exhaustion — a 0-benefit care environment pays $0
      // while the pool is still live, and there the reserve must still drain.
      policyExhausted,
    });
    const waitingPeriodSurplusApplied = applyOpeningSurplusToCost({
      openSurplus: openState.strategyState.indemnitySurplus,
      alreadyApplied: surplusResult.surplusApplied,
      remainingCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      enabled: isIndemnity && wpDaysThisRow > 0,
      policyExhausted,
    });
    const totalSurplusApplied = surplusResult.surplusApplied + waitingPeriodSurplusApplied;
    const totalPayoutToCost = surplusResult.ltcPayoutToCost + waitingPeriodSurplusApplied;
    const closeSurplus = surplusResult.closeSurplus - waitingPeriodSurplusApplied;

    // 5. Closing balances. Joint: draw the active claimant's INDIVIDUAL pool
    //    first, spill the remainder into the SHARED pool (legacy ShareCare:
    //    once the individual pool exhausts, further draws come from the shared
    //    pool, line ~1977-1981). The surfaced baseBenefitPool = active
    //    individual + shared, so the universal identity holds. No death
    //    benefit to deplete.
    let closeActiveIndividual = 0;
    let closeShared = 0;
    let sharedDrawnThisRow = 0;
    if (jointPoolsBefore) {
      const fromIndividual = Math.min(drawFromPool, activeIndividualAfterAccrual);
      const fromShared = drawFromPool - fromIndividual;
      closeActiveIndividual = activeIndividualAfterAccrual - fromIndividual;
      closeShared = sharedAfterAccrual - fromShared;
      sharedDrawnThisRow = fromShared;
    }
    const closePool = poolAfterAccrual - drawFromPool;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: totalPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - totalPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<TraditionalLtcDeltas> = {
      payout,
      base: { depletion: drawFromPool, inflationAccrual },
      // No second pool — EOB flow is always zero.
      eob: { depletion: 0, inflationAccrual: 0 },
      // No death benefit on traditional LTC.
      deathBenefit: { reduction: 0, flooredAtMinimum: false },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: totalSurplusApplied,
      },
    };

    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };

    // Joint: update only the active claimant's EP balance; ShareCare also
    // carries the other spouse's individual pool forward (with its own
    // accrual) while the shared pool is common. Generic joint has no
    // sub-pools — the single baseBenefitPool is the whole story.
    const closeStrategyState: TraditionalLtcState = jointWaitingPeriodBefore
      ? {
          indemnitySurplus: closeSurplus,
          ...(jointPoolsBefore
            ? {
                jointPools: {
                  clientIndividual:
                    activeClaimant === 'client'
                      ? closeActiveIndividual
                      : clientIndividualAfterAccrual,
                  partnerIndividual:
                    activeClaimant === 'partner'
                      ? closeActiveIndividual
                      : partnerIndividualAfterAccrual,
                  shared: closeShared,
                },
                sharedPoolDrawnToDate:
                  (openState.strategyState.sharedPoolDrawnToDate ?? 0) + sharedDrawnThisRow,
              }
            : {}),
          jointWaitingPeriod: {
            ...jointWaitingPeriodBefore,
            [activeClaimant]: activeWaitingPeriodAfter,
          },
        }
      : { indemnitySurplus: closeSurplus };

    const closeState: MonthlyState<TraditionalLtcState> = {
      pools: {
        baseBenefitPool: closePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      strategyState: closeStrategyState,
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closePool <= 0,
      clientPoolExhausted: closePool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    // Transition milestones compare against `priorClaimantMonth` — the prior
    // row of THIS spouse — not the global previous row, which on a joint policy
    // is often the other spouse (the merged stream interleaves them when their
    // care overlaps) and would fire a spurious transition on every interleaved
    // row. The pool inflation accrual above keeps using the global `priorMonth`
    // so it fires once per shared policy-year boundary. For single-life
    // priorClaimantMonth === priorMonth.
    const milestones = deriveStandardMilestones<TraditionalLtcState, TraditionalLtcDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: isJoint ? priorClaimantMonth : priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: 0,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: inflation.rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<TraditionalLtcState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<TraditionalLtcState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // Traditional LTC has no death benefit — always 0.
  finalizeDeathBenefit(_input: FinalizeDeathBenefitInput<TraditionalLtcState>): number {
    return 0;
  },
};
